// @flow

import React, { Fragment } from 'react';
import type { ComponentType } from 'react';
import { I18n } from '@lingui/react';
import styled, { type StyledComponent } from 'styled-components';

import { MQ, DSToken } from '@rover/react-lib/styles';
import LabelAndErrorFormGroup from '@rover/react-lib/components/utils/LabelAndErrorFormGroup';
import CreditCardIcon from '@rover/react-lib/components/typography/CreditCardIcon';
import type { CreditCardType } from '@rover/react-lib/types/CreditCard';
import CreditCardNameField from './CreditCardNameField';

const CreditCardNumberInputContainer: StyledComponent<{}> = styled.div`
  flex-grow: 1;
  ${MQ.NOT_XS} {
    flex-basis: 400px;
  }
`;

const ExpirationInputContainer: StyledComponent<{}> = styled.div`
  flex-grow: 1;
  ${MQ.NOT_XS} {
    flex-basis: 120px;
  }
`;

const CVVInputContainer: StyledComponent<{}> = styled.div`
  flex-grow: 1;
  ${MQ.NOT_XS} {
    flex-basis: 120px;
  }
`;

const InputWrapper: StyledComponent<{}> = styled.div`
  display: flex;
  ${MQ.NOT_XS} {
    flex-direction: row;
    div + div {
      margin-left: 15px;
    }
  }
  ${MQ.XS_DOWN} {
    flex-direction: column;
  }
`;

const getBorderColor = ({ isFocus }) => {
  if (isFocus) return `1px solid ${DSToken.BORDER_COLOR_INPUT_FOCUS}`;
  return `1px solid ${DSToken.BORDER_COLOR_PRIMARY}`;
};

export const InputPlaceHolder: ComponentType<{ isFocus: boolean }> = styled.div`
  height: 40px;
  padding: 8px;
  box-shadow: none;
  border-radius: 4px;
  display: flex;
  border: ${getBorderColor};
`;

const CardNumberPlaceHolder: StyledComponent<{}> = styled.div`
  flex-grow: 1;
  height: 25px;
`;

const ExpirationAndCvvContainer: StyledComponent<{}> = styled.div`
  flex-direction: row;
  display: flex;
  ${MQ.NOT_XS} {
    flex-basis: 260px;
    flex-grow: 1;
  }
  ${MQ.XS_DOWN} {
    div + div {
      margin-left: 15px;
    }
  }
`;

type Props = {
  form: any,
  cardType: ?CreditCardType,
  currentFocus: string,
};

const CreditCardForm = ({ form, cardType, currentFocus }: Props) => (
  <I18n>
    {({ i18n }) => (
      <Fragment>
        <CreditCardNameField
          value={form.data.creditCard.cardholderName}
          onChange={cardholderName => form.onChange({ creditCard: { cardholderName } })}
          validationError={form.validationErrors.creditCard.cardholderName}
          data-qa-id="input-text-credit-card-name-on-card"
        />
        <InputWrapper>
          <CreditCardNumberInputContainer>
            <LabelAndErrorFormGroup
              label={i18n._('Card Number')}
              validationError={form.validationErrors.creditCard.creditCardNumber}
            >
              {() => (
                <InputPlaceHolder isFocus={currentFocus === 'number'}>
                  <CardNumberPlaceHolder id="card-number" />
                  <CreditCardIcon type={cardType || null} />
                </InputPlaceHolder>
              )}
            </LabelAndErrorFormGroup>
          </CreditCardNumberInputContainer>
          <ExpirationAndCvvContainer>
            <ExpirationInputContainer>
              <LabelAndErrorFormGroup
                label={i18n._('Expiration')}
                validationError={form.validationErrors.creditCard.expiration}
                InputComponent={InputPlaceHolder}
                id="expiration"
                isFocus={currentFocus === 'expirationDate'}
              />
            </ExpirationInputContainer>
            <CVVInputContainer>
              <LabelAndErrorFormGroup
                label="CVV"
                validationError={form.validationErrors.creditCard.cvv}
                InputComponent={InputPlaceHolder}
                id="cvv"
                isFocus={currentFocus === 'cvv'}
              />
            </CVVInputContainer>
          </ExpirationAndCvvContainer>
        </InputWrapper>
      </Fragment>
    )}
  </I18n>
);

export default CreditCardForm;



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/CreditCardForm/BraintreeCreditCardForm/CreditCardForm.jsx